import type { BuildingHeight, BuildingMaterial, SpriteId } from './contracts.js';
import type { RuntimeAtlas as Atlas } from './atlasContract.js';
import { fnv1a, TERRAIN_CHUNK_TILES, terrainTileAt, treeAtTile, type TerrainRaster } from './terrain.js';
import { rotate, tileToScreen, type Rotation, type Zoom } from './projection.js';
import { clearedBuildingTiles, NO_CLEARED_TILES, raisedStructureVolumes, type ClearanceIndex, type ClearedTiles } from './buildingClearance.js';

export interface CityDrawableSeed {
  readonly spriteId: SpriteId; readonly tile: readonly [number, number]; readonly height: 0; readonly stableId: string;
  readonly rotatedX: number; readonly rotatedY: number; readonly sortKey: number; readonly screen: readonly [number, number];
  readonly boundsOffset: readonly [number, number, number, number]; readonly rect: readonly [number, number, number, number]; readonly anchor: readonly [number, number];
  readonly page: number;
}
type Project = (tile: readonly [number, number], rotation: Rotation, zoom: Zoom, pixelRatio: number) => readonly [number, number];
const projectAtOrigin: Project = (tile, rotation, zoom, pixelRatio) => tileToScreen(tile as [number, number], { center: [0, 0], rotation, zoom: (zoom * pixelRatio) as Zoom });
const frozenTuple = <T extends readonly number[]>(value: T): T => Object.freeze([...value]) as T;
/** Compresses encoded 1..16 raster levels into the 1..12 display atlas range. */
export const buildingDisplayUnits = (height: number): BuildingHeight =>
  Math.min(12, height <= 4 ? height : 4 + Math.ceil((height - 4) / 4)) as BuildingHeight;

/** Salted independently from tree placement so city material never changes with camera state. */
export const buildingMaterialForTile = (units: BuildingHeight, tx: number, ty: number): BuildingMaterial => {
  const sample = fnv1a(`building:${tx},${ty}`) / 2 ** 32;
  if (units <= 3) return sample < .7 ? 'brick' : 'concrete';
  if (units <= 5) return sample < .6 ? 'concrete' : 'brick';
  return sample < .6 ? 'glass' : 'concrete';
};

/**
 * Building tiles a raised corridor structure stands in (buildingClearance.ts), by the drawn building height. Built once
 * per loaded composition and raster; no composition clears nothing.
 */
export function buildingClearance(composition: ClearanceIndex | undefined, raster: TerrainRaster): ClearedTiles {
  if (!composition) return NO_CLEARED_TILES;
  return clearedBuildingTiles(raisedStructureVolumes(composition), (x, y) => {
    const tile = terrainTileAt(raster, [x, y]);
    return tile?.kind === 'building' ? buildingDisplayUnits(tile.height) : undefined;
  });
}

/** Bounded projected city chunks. A pan only offsets/culls these records. */
export class CityChunks {
  private cache = new Map<string, readonly CityDrawableSeed[]>();
  constructor(private readonly raster: TerrainRaster, private readonly atlas: Atlas, private readonly maxChunks = 96, private readonly project: Project = projectAtOrigin, private readonly cleared: ClearedTiles = NO_CLEARED_TILES) {}
  clear(): void { this.cache.clear(); }
  get dimensions(): Pick<TerrainRaster, 'width' | 'height'> { return this.raster; }
  get(chunkX: number, chunkY: number, rotation: Rotation, zoom: Zoom, pixelRatio: number): readonly CityDrawableSeed[] {
    const key = `${rotation}:${zoom}:${pixelRatio}:${chunkX}:${chunkY}`;
    const prior = this.cache.get(key);
    if (prior) { this.cache.delete(key); this.cache.set(key, prior); return prior; }
    const items: CityDrawableSeed[] = [];
    const startX = chunkX * TERRAIN_CHUNK_TILES, startY = chunkY * TERRAIN_CHUNK_TILES;
    for (let y = startY; y < Math.min(startY + TERRAIN_CHUNK_TILES, this.raster.height); y += 1) for (let x = startX; x < Math.min(startX + TERRAIN_CHUNK_TILES, this.raster.width); x += 1) {
      const tile = terrainTileAt(this.raster, [x, y])!;
      const seeds: Array<Pick<CityDrawableSeed, 'spriteId'|'tile'|'height'|'stableId'>> = [];
      if (tile.kind === 'building' && !this.cleared.has(x, y)) {
        const height = buildingDisplayUnits(tile.height), material = buildingMaterialForTile(height, x, y);
        seeds.push({ spriteId: `building/${material}/${height}`, tile: [x + .5, y + .5], height: 0, stableId: `building:${x},${y}` });
      }
      const tree = treeAtTile(this.raster, [x, y]);
      if (tree) seeds.push({ spriteId: 'scenery/tree', tile: tree.tile, height: 0, stableId: tree.stableId });
      for (const seed of seeds) {
        const frame = this.atlas.sprites[seed.spriteId].frames[0]!, projected = this.project(seed.tile, rotation, zoom, pixelRatio), rotated = rotate(seed.tile as [number, number], rotation);
        const scale = zoom * pixelRatio / (this.atlas.level ?? 1);
        items.push(Object.freeze({ ...seed, tile: frozenTuple(seed.tile), rotatedX: rotated[0], rotatedY: rotated[1], sortKey: rotated[0] + rotated[1], screen: frozenTuple(projected), boundsOffset: frozenTuple([-frame.anchor[0] * scale, -frame.anchor[1] * scale, (frame.rect[2] - frame.anchor[0]) * scale, (frame.rect[3] - frame.anchor[1]) * scale] as [number, number, number, number]), rect: frozenTuple(frame.rect), anchor: frozenTuple(frame.anchor), page: frame.page }));
      }
    }
    const value = Object.freeze(items);
    this.cache.set(key, value);
    while (this.cache.size > this.maxChunks) this.cache.delete(this.cache.keys().next().value!);
    return value;
  }
  get size(): number { return this.cache.size; }
}
